import React from "react";
import { Flame, Leaf, Sparkles, Star, Crown, BadgePercent } from "lucide-react";
import { api } from "../../api";

// Commercial badges (filled, brand tones) vs product attributes (outlined, neutral). Codes come from the manager.
export const COMMERCIAL = {
  consigliato: { label: "Consigliato", Icon: Sparkles, cls: "bg-amber-500 text-white" },
  specialita: { label: "Specialità", Icon: Crown, cls: "bg-[#123C46] text-white" },
  best_seller: { label: "Best seller", Icon: Star, cls: "bg-rose-600 text-white" },
  novita: { label: "Novità", Icon: Sparkles, cls: "bg-sky-600 text-white" },
  promo: { label: "Promo", Icon: BadgePercent, cls: "bg-red-600 text-white" },
};
export const ATTRIBUTES = {
  piccante: { label: "Piccante", Icon: Flame, cls: "text-orange-700 border-orange-200 bg-orange-50/60" },
  vegetariano: { label: "Vegetariano", Icon: Leaf, cls: "text-emerald-700 border-emerald-200 bg-emerald-50/60" },
};
export const BADGE_LABELS = { ...Object.fromEntries(Object.entries(COMMERCIAL).map(([k, v]) => [k, v.label])), ...Object.fromEntries(Object.entries(ATTRIBUTES).map(([k, v]) => [k, v.label])) };
export const HIGHLIGHT = Object.keys(COMMERCIAL);

export const ytId = (url = "") => { const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([\w-]{11})/); return m ? m[1] : null; };
export const mediaUrl = (ref, w) => (ref && /^https?:\/\//.test(ref) ? ref : ref ? api.mediaUrl(ref, w) : null);
export const dishPhoto = (d, w = 160) => {
  const photos = (d.media || []).filter((x) => x.kind === "image");
  const main = photos.find((p) => p.id === d.main) || photos[0];
  return main ? api.mediaUrl(main.id, w) : null;
};
export const dishVideo = (d) => (d.media || []).find((x) => x.kind === "video");
export const hasVideo = (d) => !!dishVideo(d) || !!d.external_video;
export const isFeatured = (d) => d.recommended || (d.badges || []).some((b) => HIGHLIGHT.includes(b));
export const splitBadges = (badges = []) => ({ commercial: badges.filter((b) => COMMERCIAL[b]), attrs: badges.filter((b) => ATTRIBUTES[b]) });

export function CommercialBadge({ code, size = "sm" }) {
  const b = COMMERCIAL[code];
  if (!b) return null;
  const { Icon } = b;
  return (
    <span data-testid={`badge-${code}`} className={`inline-flex items-center gap-1 rounded-full font-bold uppercase tracking-wide shadow-sm ${b.cls} ${size === "xs" ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-0.5 text-[10px]"}`}>
      <Icon className={size === "xs" ? "h-2.5 w-2.5" : "h-3 w-3"} /> {b.label}
    </span>
  );
}

export function AttributePill({ code }) {
  const a = ATTRIBUTES[code];
  if (!a) return null;
  const { Icon } = a;
  return (
    <span data-testid={`attr-${code}`} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-medium ${a.cls}`}>
      <Icon className="h-3 w-3" /> {a.label}
    </span>
  );
}

// Deliberate, elegant no-photo fallback: brand tint + dot pattern + icon + category initial.
export function PhotoFallback({ category, className = "", iconClass = "h-6 w-6" }) {
  return (
    <div data-testid="photo-fallback" className={`relative overflow-hidden bg-[#e9f1ef] text-[#123C46] ${className}`}>
      <div className="absolute inset-0 opacity-[0.35]" style={{ backgroundImage: "radial-gradient(#123C46 0.8px, transparent 0.8px)", backgroundSize: "9px 9px" }} />
      <div className="absolute inset-0 grid place-items-center">
        <svg viewBox="0 0 24 24" className={`${iconClass} opacity-70`} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="m16 2-2.3 2.3a3 3 0 0 0 0 4.2l1.8 1.8a3 3 0 0 0 4.2 0L22 8" /><path d="M15 15 3.3 3.3a4.2 4.2 0 0 0 0 6l7.3 7.3c.7.7 2 .7 2.8 0L15 15Zm0 0 7 7" /><path d="m2.1 21.8 6.4-6.3" /><path d="m19 5-7 7" /></svg>
      </div>
      {category && <span className="absolute bottom-1 right-1.5 text-[9px] font-bold uppercase tracking-wider opacity-60 truncate max-w-[90%]">{category}</span>}
    </div>
  );
}
